/**
 * BhiAuditModal.jsx — iter750 (Feb 2026)
 *
 * Full BHI Signal Audit — one-glance transparency panel showing every
 * real signal used in the current snapshot + missing items per section.
 * Opened via the "🔍 Одит на данните" button in the BHI page header.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, ShieldCheck, CheckCircle2, Circle, AlertCircle } from "lucide-react";
import { useAuth } from "@/App";

const CONF_COLORS = {
  high: "text-emerald-600 dark:text-emerald-300",
  mid:  "text-amber-600 dark:text-amber-300",
  low:  "text-orange-600 dark:text-orange-300",
  none: "text-slate-500 dark:text-slate-400",
};

const BAR_BG = {
  high: "bg-emerald-500",
  mid:  "bg-amber-500",
  low:  "bg-orange-500",
  none: "bg-slate-300 dark:bg-slate-700",
};

export default function BhiAuditModal({ open, onClose, isDark = false }) {
  const { API, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onEsc);
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/breast-intelligence/audit`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; document.removeEventListener("keydown", onEsc); };
  }, [open, API, user, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="bhi-audit-overlay"
    >
      <div
        className={`my-8 w-full max-w-3xl rounded-2xl border shadow-2xl ${isDark ? "border-white/10 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-800"}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="bhi-audit-modal"
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-2xl border-b border-slate-200 bg-inherit px-5 py-3 dark:border-white/10">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-pink-500" />
            <h2 className="text-lg font-bold">Одит на данните</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="bhi-audit-close"
            className={`inline-flex items-center justify-center rounded-md p-1.5 transition ${isDark ? "text-slate-400 hover:bg-white/10 hover:text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}
            aria-label="Затвори">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="p-5">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="bhi-audit-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Извличаме одит на всичките 9 секции...
            </div>
          )}
          {err && !loading && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
              Грешка: {err}
            </div>
          )}
          {data && !loading && (
            <>
              {/* Overall summary */}
              <div className={`mb-5 rounded-xl border p-4 ${isDark ? "border-white/10 bg-slate-800/40" : "border-slate-200 bg-slate-50"}`} data-testid="bhi-audit-summary">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Реални сигнали</div>
                    <div className="text-2xl font-bold">{data.total_signals}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Активни секции</div>
                    <div className="text-2xl font-bold">{data.active_sections}/{data.total_sections}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Средна пълнота</div>
                    <div className="text-2xl font-bold">{data.avg_completeness_pct}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Доверие</div>
                    <div className={`text-2xl font-bold uppercase ${CONF_COLORS[data.overall_confidence]}`}>{data.overall_confidence}</div>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">{data.summary_bg}</p>

                {/* Derived state pills */}
                {data.derived_state && (
                  <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                    <span className="rounded-full bg-white px-2 py-0.5 shadow-sm dark:bg-slate-900">
                      Tissue среди: {data.derived_state.biology_active_zones}/{data.derived_state.biology_total_zones}
                    </span>
                    <span className="rounded-full bg-white px-2 py-0.5 shadow-sm dark:bg-slate-900">
                      Метаболитни компоненти: {data.derived_state.metabolic_measured_components}/{data.derived_state.metabolic_total_components}
                    </span>
                    {data.derived_state.metabolic_total_score != null && (
                      <span className="rounded-full bg-white px-2 py-0.5 shadow-sm dark:bg-slate-900">
                        Обобщен индекс: {data.derived_state.metabolic_total_score}/100
                      </span>
                    )}
                    {data.derived_state.tissue_age != null && (
                      <span className="rounded-full bg-white px-2 py-0.5 shadow-sm dark:bg-slate-900">
                        Метаболитна възраст: {data.derived_state.tissue_age} г.
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Section-by-section */}
              <div className="space-y-2" data-testid="bhi-audit-sections">
                {(data.sections || []).map((s) => (
                  <div
                    key={s.id}
                    className={`rounded-xl border p-3 ${isDark ? "border-white/10 bg-slate-800/40" : "border-slate-200 bg-white"}`}
                    data-testid={`bhi-audit-section-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="text-sm font-semibold">{s.label_bg}</div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold uppercase ${CONF_COLORS[s.confidence]}`}>{s.confidence}</span>
                        <span className="text-xs text-slate-500">{s.completeness_pct}%</span>
                      </div>
                    </div>
                    <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div className={`h-full ${BAR_BG[s.confidence]} transition-all`} style={{ width: `${s.completeness_pct}%` }} />
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs">
                      <div>
                        <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-300">
                          Измерено ({s.measured_signals.length})
                        </div>
                        {s.measured_signals.length === 0 ? (
                          <div className="text-slate-400">—</div>
                        ) : (
                          <ul className="space-y-0.5">
                            {s.measured_signals.map((sig, i) => (
                              <li key={i} className="flex items-start gap-1 text-slate-700 dark:text-slate-200">
                                <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500 mt-0.5" />
                                <span>{sig}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                          Липсва ({s.missing_signals.length})
                        </div>
                        {s.missing_signals.length === 0 ? (
                          <div className="text-slate-400">—</div>
                        ) : (
                          <ul className="space-y-0.5">
                            {s.missing_signals.map((sig, i) => (
                              <li key={i} className="flex items-start gap-1 text-slate-500 dark:text-slate-400">
                                <Circle className="h-3 w-3 shrink-0 mt-0.5" />
                                <span>{sig}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-start gap-2 rounded-md bg-slate-50 p-2 text-[11px] text-slate-600 dark:bg-slate-800/40 dark:text-slate-400">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Този одит показва точно кои сигнали захранват Вашия BHI snapshot. Ниска пълнота = по-широки доверителни интервали. Добавете липсващите източници, за да усилите клиничната стойност на отчета.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
